import React, { useEffect, useRef, useState, useCallback } from 'react'

const modalStack = []

function pushModal(id) {
  modalStack.push(id)
}

function removeModal(id) {
  const index = modalStack.lastIndexOf(id)
  if (index !== -1) modalStack.splice(index, 1)
}

function isTopModal(id) {
  return modalStack.length > 0 && modalStack[modalStack.length - 1] === id
}

function getModalStyle() {
  const viewport = window.innerWidth

  let style = {
    background: '#ededed',
    padding: 24,
    border: '1px solid #107c6b',
    borderRadius: 12,
    boxShadow: '0 12px 28px rgba(0,0,0,0.65)',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '90vh',
    overflow: 'hidden'
  }

  if (viewport >= 2560) {
    style = { ...style, width: '30vw', height: 'min(85vh, 40vw)' }
  } else if (viewport >= 1920) {
    style = { ...style, width: '35vw', height: 'min(85vh, 60vw)' }
  } else if (viewport >= 1366) {
    style = { ...style, width: '40vw', height: 'min(95vh, 40vw)' }
  } else {
    style = { ...style, width: '90vw', height: 'min(90vh, 80vw)' }
  }

  return style
}

export default function ModalLayout({
  open,
  onClose,
  children,
  size = 'large',
  zIndex = 3000,
  closeOnEscape = true
}) {
  const idRef = useRef(Symbol('modal'))

  const computeStyle = useCallback(() => {
    const base = getModalStyle()

    if (size === 'small') {
      return { ...base, width: '420px', height: 'auto' }
    }

    if (size === 'medium') {
      return { ...base, width: '650px', height: 'auto' }
    }

    if (size === 'fullscreen') {
      return { ...base, width: '95vw', height: '95vh', maxHeight: '90vh' }
    }

    if (size === 'max') {
      return {
        background: '#ededed',
        padding: 20,
        border: 'none',
        borderRadius: 0,
        boxShadow: 'none',
        display: 'flex',
        flexDirection: 'column',
        width: '100vw',
        height: '100vh'
      }
    }

    return base
  }, [size])

  const [modalStyle, setModalStyle] = useState(() => computeStyle())

  // update pri open/size
  useEffect(() => {
    if (!open) return
    setModalStyle(computeStyle())
  }, [open, computeStyle])

  // modal stack
  useEffect(() => {
    if (!open) return

    const id = idRef.current
    pushModal(id)
    return () => removeModal(id)
  }, [open])

  // resize
  useEffect(() => {
    const handleResize = () => {
      if (!open) return
      setModalStyle(computeStyle())
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [open, computeStyle])

  // ESC
  useEffect(() => {
    if (!open) return

    const id = idRef.current

    const handleKeyDown = (e) => {
      if (e.key !== 'Escape') return
      if (!closeOnEscape) return
      if (!isTopModal(id)) return
      e.preventDefault()
      e.stopPropagation()
      onClose()
    }

    window.addEventListener('keydown', handleKeyDown, true)
    return () => window.removeEventListener('keydown', handleKeyDown, true)
  }, [open, onClose, closeOnEscape])

  if (!open) return null

  return (
    <div
      onClick={(e) => {
        if (e.target !== e.currentTarget) return
        if (!isTopModal(idRef.current)) return
        onClose()
      }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex
      }}
    >
<div onClick={(e) => e.stopPropagation()} style={modalStyle}>
  {children}
</div>
    </div>
  )
}